// ── ExportPreviewOverlay — Modal overlay for step 2 (preview before export) ─
import React, { useEffect, useState, useRef } from 'react';
import { useEscapeLayer } from '@/shared/hooks/useEscapeLayer';
import {
  Eye,
  ArrowRightLeft,
  Loader2,
} from 'lucide-react';
import type {
  PreviewSummary,
  EnvironmentDef,
  ExportPreviewCounts,
} from '../types';
import { previewExportCounts } from '../../../api/scopeMigrateApi';
import { scopeKeyDisplay } from '../utils';
import type { WorkflowScope } from '../types';

import { fmtBytes as formatBytes } from '@/utils/format';

interface ExportPreviewOverlayProps {
  previewSummary: PreviewSummary;
  sourceEnv: string;
  targetEnv: string;
  getEnvDef: (key: string) => EnvironmentDef | undefined;

  /** Root scope IDs selected for export — drives server-side preview counts. */
  rootScopeIds: number[];
  /** Active tenant used for the export scope filter. */
  tenantId: number;

  // Filter options
  includeChildren: boolean;
  setIncludeChildren: (v: boolean) => void;
  filterMaxDepth: number;
  setFilterMaxDepth: (v: number) => void;
  includeOutcomes: boolean;
  setIncludeOutcomes: (v: boolean) => void;
  includeHotStream: boolean;
  setIncludeHotStream: (v: boolean) => void;

  // Actions
  canExport: boolean;
  exportPending: boolean;
  onExport: () => void;
  onBack: () => void;
}

export function ExportPreviewOverlay({
  previewSummary,
  sourceEnv,
  targetEnv,
  getEnvDef,
  rootScopeIds,
  tenantId,
  includeChildren,
  setIncludeChildren,
  filterMaxDepth,
  setFilterMaxDepth,
  includeOutcomes,
  setIncludeOutcomes,
  includeHotStream,
  setIncludeHotStream,
  canExport,
  exportPending,
  onExport,
  onBack,
}: ExportPreviewOverlayProps) {
  useEscapeLayer(onBack, !exportPending);

  // ── Server-side preview counts (accurate DB row counts) ──────────────
  const [serverCounts, setServerCounts] = useState<ExportPreviewCounts | null>(null);
  const [countsLoading, setCountsLoading] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    if (rootScopeIds.length === 0) return;
    const id = ++reqId.current;
    setCountsLoading(true);
    setServerCounts(null);
    previewExportCounts({
      root_scope_ids: rootScopeIds,
      tenant_id: tenantId,
      include_children: includeChildren,
      max_depth: filterMaxDepth,
      include_outcomes: includeOutcomes,
      include_hot_stream: includeHotStream,
    })
      .then((counts) => { if (id === reqId.current) setServerCounts(counts); })
      .catch(() => { /* keep heuristic fallback */ })
      .finally(() => { if (id === reqId.current) setCountsLoading(false); });
  }, [rootScopeIds, tenantId, includeChildren, filterMaxDepth, includeOutcomes, includeHotStream]);

  /** Render a count value with a loading spinner fallback. */
  const countCell = (value: number | undefined, fallback: number | string | undefined) => {
    if (countsLoading) return <Loader2 className="w-3 h-3 animate-spin text-neutral-500" />;
    return value != null ? value : (fallback ?? '—');
  };
  return (
    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm z-10 flex items-center justify-center p-4">
      <div className="pac-glass-raised rounded-lg p-5 max-w-lg w-full space-y-4 shadow-2xl max-h-[90%] overflow-y-auto">
        <div className="flex items-center gap-2 text-sm font-medium text-neutral-100">
          <Eye className="w-4 h-4 text-sky-400" />
          Export Preview
        </div>

        <div className="space-y-3 text-xs">
          {/* Source → Target envs */}
          <div className="flex items-center justify-between">
            <span className="text-neutral-500">Source</span>
            <span className="text-neutral-200 bg-black border border-neutral-700 rounded px-2 py-0.5">
              {getEnvDef(sourceEnv)?.label ?? sourceEnv}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-neutral-500">Target</span>
            <span className="text-neutral-200 bg-black border border-neutral-700 rounded px-2 py-0.5">
              {getEnvDef(targetEnv)?.label ?? targetEnv}
            </span>
          </div>

          <div className="border-t border-neutral-800" />

          {/* Transfer summary stats */}
          <div>
            <div className="text-neutral-500 mb-1.5">Transfer summary</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="pac-glass rounded px-2 py-1.5">
                <div className="text-[9px] text-neutral-600 uppercase tracking-wider">Selected roots</div>
                <div className="text-sm text-neutral-200 font-medium tabular-nums">{previewSummary.rootCount}</div>
              </div>
              <div className="pac-glass rounded px-2 py-1.5">
                <div className="text-[9px] text-neutral-600 uppercase tracking-wider">Total scopes</div>
                <div className="text-sm text-neutral-200 font-medium tabular-nums">{previewSummary.totalScopes}</div>
              </div>
              <div className="pac-glass rounded px-2 py-1.5">
                <div className="text-[9px] text-neutral-600 uppercase tracking-wider">Tree depth</div>
                <div className="text-sm text-neutral-200 font-medium tabular-nums">{previewSummary.treeDepth}</div>
              </div>
              <div className="pac-glass rounded px-2 py-1.5">
                <div className="text-[9px] text-neutral-600 uppercase tracking-wider">Namespaces</div>
                <div className="text-sm text-neutral-200 font-medium tabular-nums">{previewSummary.distinctNamespaces.length}</div>
              </div>
              {includeOutcomes && (
                <div className="pac-glass rounded px-2 py-1.5">
                  <div className="text-[9px] text-emerald-700 uppercase tracking-wider">Outcomes</div>
                  <div className="text-sm text-emerald-300 font-medium tabular-nums">
                    {countCell(serverCounts?.outcome_count, previewSummary.estimatedOutcomeCount)}
                  </div>
                </div>
              )}
              {includeHotStream && (
                <div className="pac-glass rounded px-2 py-1.5">
                  <div className="text-[9px] text-amber-700 uppercase tracking-wider">Hot Stream</div>
                  <div className="text-sm text-amber-300 font-medium tabular-nums">
                    {countCell(serverCounts?.hot_stream_count, previewSummary.estimatedHotStreamCount)}
                  </div>
                </div>
              )}
              {/* GCS artifact files — server-side count or fallback */}
              {(includeOutcomes || (serverCounts && serverCounts.gcs_file_count > 0)) && (
                <div className="pac-glass rounded px-2 py-1.5">
                  <div className="text-[9px] text-sky-700 uppercase tracking-wider">Artifacts (GCS)</div>
                  <div className="text-sm text-sky-300 font-medium tabular-nums">
                    {countCell(serverCounts?.gcs_file_count, previewSummary.artifactFileCount > 0 ? previewSummary.artifactFileCount : '—')}
                  </div>
                  {previewSummary.artifactTotalBytes > 0 && (
                    <div className="text-[9px] text-neutral-500 tabular-nums">
                      {formatBytes(previewSummary.artifactTotalBytes)}
                    </div>
                  )}
                </div>
              )}
              {/* Archive file groups */}
              {serverCounts && serverCounts.archive_file_count > 0 && (
                <div className="pac-glass rounded px-2 py-1.5">
                  <div className="text-[9px] text-violet-700 uppercase tracking-wider">Archives</div>
                  <div className="text-sm text-violet-300 font-medium tabular-nums">
                    {serverCounts.archive_file_count}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-1.5 text-[9px] text-neutral-600">
              {includeChildren
                ? filterMaxDepth > 0
                  ? `Exporting subtree up to depth ${filterMaxDepth}.`
                  : 'Exporting full subtree (all descendants).'
                : 'Exporting root scope only (no children).'}
              {includeOutcomes ? ' Outcomes included.' : ' Outcomes excluded.'}
              {includeHotStream ? ' Hot stream included.' : ''}
            </div>
          </div>

          {/* Per-namespace breakdown */}
          <div>
            <div className="text-neutral-500 mb-1">
              Namespaces ({previewSummary.distinctNamespaces.length})
            </div>
            <div className="pac-glass rounded p-2 space-y-1 max-h-40 overflow-y-auto">
              {previewSummary.namespaceCounts.map(({ ns, count }) => (
                <div key={ns} className="flex items-center justify-between text-[11px]">
                  <span className="text-neutral-300 font-mono truncate">{ns}</span>
                  <span className="text-neutral-500 tabular-nums shrink-0 ml-2">
                    {count} scope{count !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Root scope(s) sample */}
          <div>
            <div className="text-neutral-500 mb-1">
              {previewSummary.rootCount === 1 ? 'Root scope' : `Root scopes (${previewSummary.rootCount})`}
            </div>
            <div className="pac-glass rounded p-2 space-y-1 max-h-28 overflow-y-auto">
              {previewSummary.rootDisplays.map((d, i) => (
                <div key={i} className="text-neutral-200 font-mono text-[11px] truncate">{d}</div>
              ))}
              {previewSummary.rootCount > 5 && (
                <div className="text-[9px] text-neutral-600">…and {previewSummary.rootCount - 5} more</div>
              )}
            </div>
          </div>

          {/* Export filter controls */}
          <div>
            <div className="text-neutral-500 mb-1.5">Export options</div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={includeChildren}
                  onChange={(e) => setIncludeChildren(e.target.checked)}
                  className="accent-emerald-500 w-3 h-3"
                />
                <span className="text-neutral-300 group-hover:text-neutral-100 transition-colors">
                  Include children
                </span>
              </label>

              {includeChildren && (
                <div className="flex items-center gap-2 pl-5">
                  <span className="text-neutral-500 shrink-0">Max depth</span>
                  <input
                    type="number"
                    min={0}
                    value={filterMaxDepth}
                    onChange={(e) => setFilterMaxDepth(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-16 px-1.5 py-0.5 rounded pac-glass-input text-neutral-200 text-center tabular-nums outline-none focus:border-neutral-500"
                  />
                  <span className="text-neutral-600 text-[9px]">0 = unlimited</span>
                </div>
              )}

              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={includeOutcomes}
                  onChange={(e) => setIncludeOutcomes(e.target.checked)}
                  className="accent-emerald-500 w-3 h-3"
                />
                <span className="text-neutral-300 group-hover:text-neutral-100 transition-colors">
                  Include outcomes
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={includeHotStream}
                  onChange={(e) => setIncludeHotStream(e.target.checked)}
                  className="accent-amber-500 w-3 h-3"
                />
                <span className="text-neutral-300 group-hover:text-neutral-100 transition-colors">
                  Include hot stream data
                </span>
                <span className="text-[9px] text-neutral-600">(live + archived)</span>
              </label>
            </div>
          </div>

          {/* Child scope listing */}
          {previewSummary.childCount > 0 && (
            <div>
              <div className="text-neutral-500 mb-1">
                Child scopes ({previewSummary.childCount})
              </div>
              <div className="pac-glass rounded p-2 space-y-0.5 max-h-32 overflow-y-auto">
                {previewSummary.children.map((c: WorkflowScope, i: number) => (
                  <div key={i} className="text-[10px] text-neutral-400 font-mono truncate">
                    {scopeKeyDisplay(c)}
                  </div>
                ))}
                {previewSummary.childCount > 10 && (
                  <div className="text-[9px] text-neutral-600">
                    …and {previewSummary.childCount - 10} more
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            className="px-3 py-1.5 rounded border border-neutral-700 text-xs text-neutral-300 hover:border-neutral-500"
            onClick={onBack}
          >
            Cancel
          </button>
          <button
            className={
              'px-3 py-1.5 rounded border text-xs flex items-center gap-1.5 ' +
              (canExport
                ? 'border-emerald-700 text-emerald-200 hover:border-emerald-500'
                : 'border-neutral-800 text-neutral-600 cursor-not-allowed')
            }
            disabled={!canExport}
            onClick={onExport}
          >
            <ArrowRightLeft className="w-3 h-3" />
            {exportPending ? 'Exporting…' : 'Confirm Export'}
          </button>
        </div>
      </div>
    </div>
  );
}
